import React, { useState, useEffect } from 'react';
import { auth, firestore } from '../firebase/config';
import { doc, setDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useToast } from '../components/ToastContext';
import { encryptPassword, isPasswordHashed, encryptForAdmin } from '../utils/passwordUtils';

function AddEmployee() {
  const [form, setForm] = useState({
    fullName: '',
    position: '',
    email: '',
    contactNumber: '',
    cnicNo: '',
    salary: '',
    paidSalary: '',
    remainingSalary: '',
    password: '',
    role: 'employee', // Add role to form state
  });
  const [loading, setLoading] = useState(false);
  const [buttonState, setButtonState] = useState('idle'); // 'idle', 'loading', 'success'
  const [existingEmployeeData, setExistingEmployeeData] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const role = params.get('role') || form.position || '';
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (id) {
      // Edit mode: fetch employee data
      const fetchEmployee = async () => {
        setLoading(true);
        try {
          const empDoc = await getDoc(doc(firestore, 'employees', id));
          if (empDoc.exists()) {
            const data = empDoc.data();
            setExistingEmployeeData(data); // Store existing data for later use
            setForm({
              fullName: data.fullName || '',
              position: data.position || '',
              email: data.email || '',
              contactNumber: data.contactNumber || '',
              cnicNo: data.cnicNo || '',
              salary: data.salary || '',
              paidSalary: data.paidSalary || '',
              remainingSalary: data.remainingSalary || '',
              password: data.password || '', // Load password in edit mode
              role: data.role || 'employee',
              // Add internee-specific fields
              fatherName: data.fatherName || '',
              internField: data.internField || '',
              dateJoining: data.dateJoining || '',
            });
          }
        } catch (err) {
          showError('Error fetching employee: ' + err.message);
        }
        setLoading(false);
      };
      fetchEmployee();
    }
  }, [id]);

  useEffect(() => {
    // Auto-calculate remaining salary
    const salary = parseFloat(form.salary) || 0;
    const paid = parseFloat(form.paidSalary) || 0;
    const remaining = salary - paid;
    setForm(prev => ({ ...prev, remainingSalary: remaining > 0 ? remaining.toString() : '0' }));
  }, [form.salary, form.paidSalary]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setButtonState('loading');
    try {
      if (id) {
        // Edit mode: update employee
        const updateData = {
          ...form,
          uid: id,
        };
        
        // Check for duplicate contact number (excluding current employee)
        const employeesSnap = await getDocs(collection(firestore, 'employees'));
        const studentsSnap = await getDocs(collection(firestore, 'students'));
        const duplicateEmployee = employeesSnap.docs.find(doc => 
          doc.data().contactNumber === form.contactNumber && doc.id !== id
        );
        const duplicateStudent = studentsSnap.docs.find(doc => 
          doc.data().contactNumber === form.contactNumber
        );
        if (duplicateEmployee || duplicateStudent) {
          showError('This contact number is already registered with another user.');
          setLoading(false);
          setButtonState('idle');
          return;
        }
        
        // Handle role/position based on current position
        if (form.position === 'manager') {
          updateData.role = 'manager';
        } else if (form.position === 'internee') {
          updateData.role = 'internee';
        } else {
          updateData.role = 'employee';
        }
        
        // Update employee document
        console.log('AddEmployee: Saving updateData:', updateData);
        console.log('AddEmployee: dateJoining in updateData:', updateData.dateJoining);
        await setDoc(doc(firestore, 'employees', id), {
          ...updateData,
          createdAt: existingEmployeeData?.createdAt || new Date().toISOString(), // Preserve existing createdAt or add new one
        });
        
        // Update user document for login credentials
        const authEmail = `${form.contactNumber}@company.com`;
        
        // Encrypt password if it's not already hashed
        let encryptedPassword = form.password;
        if (!isPasswordHashed(form.password)) {
          encryptedPassword = await encryptPassword(form.password);
        }
        
        // Create admin-visible password
        const adminPassword = encryptForAdmin(form.password);
        
        await setDoc(doc(firestore, 'users', id), {
          fullName: form.fullName,
          contactNumber: form.contactNumber,
          role: updateData.role,
          uid: id,
          authEmail,
          password: encryptedPassword,
          adminPassword: adminPassword, // For admin visibility
        }, { merge: true });
        
        setButtonState('success');
        setTimeout(() => {
          showSuccess('Employee updated successfully');
          navigate('/admin?tab=members');
        }, 1000);
      } else {
        // Duplicate contact number check
        const employeesSnap = await getDocs(collection(firestore, 'employees'));
        const studentsSnap = await getDocs(collection(firestore, 'students'));
        const duplicateEmployee = employeesSnap.docs.find(doc => doc.data().contactNumber === form.contactNumber);
        const duplicateStudent = studentsSnap.docs.find(doc => doc.data().contactNumber === form.contactNumber);
        if (duplicateEmployee || duplicateStudent) {
          showError('This contact number is already registered with another user.');
          setLoading(false);
          return;
        }
        // Add mode: create new employee
        const authEmail = `${form.contactNumber}@company.com`;
        
        // Generate a unique UID for the employee
        const uid = `employee_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Encrypt password for storage
        const encryptedPassword = await encryptPassword(form.password);
        
        // Create admin-visible password
        const adminPassword = encryptForAdmin(form.password);
        
        // Create employee document first
        const employeeData = {
          ...form,
          email: '',
          authEmail,
          uid,
          password: encryptedPassword, // Store encrypted password
          adminPassword: adminPassword, // For admin visibility
          createdAt: new Date().toISOString(), // Add creation timestamp for sorting
          ...(role === 'internee' ? { position: 'internee' } : {}),
        };
        console.log('AddEmployee: Creating new employee with data:', employeeData);
        console.log('AddEmployee: dateJoining in new employee:', employeeData.dateJoining);
        await setDoc(doc(firestore, 'employees', uid), employeeData);
        
        // Create user document for login
        await setDoc(doc(firestore, 'users', uid), {
          fullName: form.fullName,
          contactNumber: form.contactNumber,
          role: form.role || (role === 'internee' ? 'internee' : 'employee'),
          uid,
          authEmail,
          password: encryptedPassword, // Store encrypted password
          adminPassword: adminPassword, // For admin visibility
        });
        setButtonState('success');
        setTimeout(() => {
          showSuccess('Employee created successfully');
          navigate('/admin?tab=members');
        }, 1000);
      }
    } catch (error) {
      showError('Error: ' + error.message);
      setButtonState('idle');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <form onSubmit={handleSubmit} className="p-8 bg-white rounded shadow max-w-md w-full">
        {/* Back Button */}
        <div className="mb-6">
          <div className="uiverse-box">
            <button 
              type="button"
              onClick={() => {
                const params = new URLSearchParams(location.search);
                if (params.get('tab') === 'members') {
                  navigate('/admin?tab=members');
                } else {
                  navigate('/admin');
                }
              }}
              className="flex items-center px-0 py-0 bg-transparent text-black rounded-full font-bold text-base gap-2 focus:outline-none border-none shadow-none"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </button>
          </div>
        </div>
        <h2 className="text-2xl font-semibold mb-6">{role === 'internee' ? (id ? 'Edit Internee' : 'Add Internee') : id ? 'Edit Employee' : 'Add Institute Member'}</h2>
        {/* Internee-specific fields */}
        {role === 'internee' ? (
          <>
            <input type="text" name="fullName" placeholder="Name" value={form.fullName} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <input type="text" name="fatherName" placeholder="Father's Name" value={form.fatherName} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <input type="text" name="contactNumber" placeholder="Contact Number" value={form.contactNumber} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <select name="internField" value={form.internField || ''} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required>
              <option value="">Select Intern Field</option>
              <option value="Web Development">Web Development</option>
              <option value="Graphic Design">Graphic Design</option>
              <option value="Digital Marketing">Digital Marketing</option>
              <option value="App Development">App Development</option>
              <option value="Other">Other</option>
            </select>
            <input type="password" name="password" placeholder={id ? "Update Password" : "Password"} value={form.password} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <input type="date" name="dateJoining" placeholder="Date of Joining" value={form.dateJoining || ''} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
          </>
        ) : (
          <>
            <input type="text" name="fullName" placeholder="Full Name" value={form.fullName} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <input type="text" name="position" placeholder="Position" value={form.position} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <input type="email" name="email" placeholder="Employee Email" value={form.email} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required disabled={!!id} />
            <input type="text" name="contactNumber" placeholder="Contact Number" value={form.contactNumber} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <input type="text" name="cnicNo" placeholder="CNIC Number" value={form.cnicNo} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <input type="number" name="salary" placeholder="Salary" value={form.salary} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <input type="number" name="paidSalary" placeholder="Paid Salary" value={form.paidSalary} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <input type="number" name="remainingSalary" placeholder="Remaining Salary" value={form.remainingSalary} readOnly className="w-full p-3 mb-4 border rounded bg-gray-100 font-semibold" />
            {/* Role selection dropdown for admin to choose employee or manager */}
            <select name="role" value={form.role} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required>
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
            </select>
            <input type="password" name="password" placeholder={id ? "Update Password" : "Password"} value={form.password} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
            <input type="date" name="dateJoining" placeholder="Date of Joining" value={form.dateJoining || ''} onChange={handleChange} className="w-full p-3 mb-4 border rounded" required />
          </>
        )}
        <button 
          type="submit" 
          className={`
            w-full py-3 font-semibold rounded-lg shadow-lg transition-all duration-300 transform
            ${buttonState === 'idle' 
              ? 'bg-blue-600 hover:bg-blue-700 hover:scale-105 text-white' 
              : buttonState === 'loading' 
                ? 'bg-blue-500 text-white cursor-not-allowed scale-95' 
                : 'bg-green-500 text-white scale-105'
            }
            ${buttonState === 'loading' ? 'animate-pulse' : ''}
          `}
          disabled={buttonState === 'loading'}
        >
          {buttonState === 'idle' && (
            <span className="flex items-center justify-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {id ? (role === 'internee' ? 'Update Internee' : 'Update Employee') : (role === 'internee' ? 'Add Internee' : 'Add Employee')}
            </span>
          )}
          {buttonState === 'loading' && (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          )}
          {buttonState === 'success' && (
            <span className="flex items-center justify-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Success!
            </span>
          )}
        </button>
      </form>
    </div>
  );
}

export default AddEmployee;
